import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { BASE_URL } from "../Constant";

const SESSION_KEY = "tdc_presence_sid";
const OWNER_KEY = "tdc_presence_owner";
const HEARTBEAT_MS = 30 * 1000;

const newSessionId = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {}
  return `sid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

// Fingerprint of the signed-in identity. The JWT signature segment is
// effectively unique per token, so two accounts never collide; guests
// (no token) get "guest".
const ownerFingerprint = () => {
  try {
    const t = localStorage.getItem("token");
    return t ? t.slice(-24) : "guest";
  } catch (e) {
    return "guest";
  }
};

// Returns this tab's presence session id, minting a fresh one whenever
// the signed-in identity changes — login, logout, or an account switch
// in the same tab. sessionStorage survives logout, so without this the
// new user's activity would keep flowing under the previous user's
// session and be logged against the wrong account.
const getSessionId = () => {
  try {
    const owner = ownerFingerprint();
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid || sessionStorage.getItem(OWNER_KEY) !== owner) {
      sid = newSessionId();
      sessionStorage.setItem(SESSION_KEY, sid);
      sessionStorage.setItem(OWNER_KEY, owner);
    }
    return sid;
  } catch (e) {
    return newSessionId();
  }
};

const isTrackable = (pathname) => {
  if (!pathname) return false;
  if (pathname.startsWith("/admin")) return false;
  if (pathname.startsWith("/factory")) return false;
  return true;
};

// Heartbeat ping for retailer presence + page-view analytics. Posts the
// current path to /presence/ping every HEARTBEAT_MS, and immediately on
// any path change. The server tracks the open path on each tab; on a
// path change it closes out the previous PageView with a duration.
//
// No token = no ping. The hook quietly does nothing for guests, admin
// routes, and factory routes.
export default function usePresence() {
  const location = useLocation();
  const lastPathRef = useRef(null);

  useEffect(() => {
    const send = () => {
      const token = (() => {
        try {
          return localStorage.getItem("token");
        } catch (e) {
          return null;
        }
      })();
      if (!token) return;
      const path = window.location.pathname + window.location.search;
      if (!isTrackable(path)) return;
      // Resolve the sessionId per ping so an account switch within this
      // tab mints a fresh session id immediately.
      const sessionId = getSessionId();
      try {
        fetch(`${BASE_URL}/presence/ping`, {
          method: "POST",
          keepalive: true,
          // Send the tdc_device cookie so the auth middleware can keep
          // Devices[].lastSeenAt fresh while the retailer is on the site.
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ sessionId, path }),
        }).catch(() => {});
      } catch (e) {}
    };

    const fullPath = location.pathname + location.search;
    if (lastPathRef.current !== fullPath) {
      lastPathRef.current = fullPath;
      send();
    }

    const id = setInterval(send, HEARTBEAT_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") send();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [location.pathname, location.search]);
}
